import {Page} from "@/components/pages";
import {FormActionButtons, FormattedFormRow, FormContainer, TextField} from "@dvrt/graphql-widgets";
import {
    Button,
    Checkbox,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Table,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import {
    useGetRandomPasswordQuery,
    useExtensionsQuery,
    GetRandomPasswordQueryHookResult,
    ExtensionsQueryHookResult,
} from "@/components/fax/queries.generated";
import FaxSettingsForm from "@/components/fax/settings/FaxSettingsForm";
import {HidePasswordIcon, OwnerIcon, ShowPasswordIcon} from "@/components/icons";
import OutgoingCallBarringWidget from "@/outgoing-call-barring/OutgoingCallBarringWidget";
import ErrorHelper from "@/utils/ErrorHelper";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import {useState} from "react";
import {DataSource, useDataSource} from "@/components/fax/settings/DataSource";
import {useBoolState} from "@/hooks";
import Loading from "@/components/breadcrumbs/Loading";
import {NonPaymentBlockingType} from "@/api";
import {EnumField} from "@dvrt/graphql-widgets";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {FormattedMessage} from "react-intl";
import {useStyles} from "@/components/call-group/members/styles";
import {showNotification} from "@/actions";
import {defineMessage} from "react-intl";
import {useDispatch} from "react-redux";
import TableRowWithErrors from "./TableRowWithErrors";
import {useDomainNameQuery} from "@/queries.generated";
import {NON_PAYMENT_BLOCKING_TYPE_MESSAGES} from "@/utils/translations";

type ContentProps = {
    dataSource: DataSource;
    randomPasswordQuery: GetRandomPasswordQueryHookResult;
    extensionsQuery: ExtensionsQueryHookResult;
    domainName: string;
};

type UpdateEmailFields = "enableBodyProcess" | "enableProgressFeedback";

const Content = ({dataSource, randomPasswordQuery, extensionsQuery, domainName}: ContentProps) => {
    const form = new FaxSettingsForm(dataSource);
    const [enabled, enable, disable] = useBoolState(true);
    const auth = useAuth();

    const domainNameContextFilter = ContextFilters.domain(domainName);

    const errorHelper = new ErrorHelper(form.errors);

    const canEdit = auth.hasPermission("edit_fax", domainNameContextFilter);
    const canEditOperator = auth.hasPermission("edit_fax_superuser", domainNameContextFilter);

    const [showPassword, setShowPassword] = useState(false);
    const [selectedAdditionalEmail, setAdditionalEmail] = useState("0");
    const {classes, cx} = useStyles();
    const dispatch = useDispatch();

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    async function submit() {
        disable();
        if (await form.submit()) {
            dispatch(showNotification(defineMessage({id: "fax_updated", defaultMessage: "Fax updated."}), "success"));
            form.newInitialData(await dataSource.refetch());
        }
        enable();
    }

    function generateRandomPassword() {
        randomPasswordQuery.refetch();
        form.updateValues({password: randomPasswordQuery.data.sipUser.password});
    }

    function addEmail() {
        const newEmail = extensionsQuery.data.users.items.find(
            (ext) => ext.number.shortNumber === selectedAdditionalEmail,
        );
        const newData = {
            ...form.values,
            ...{
                additionalFaxSendingEmails: {
                    emails: [
                        ...form.values.additionalFaxSendingEmails.emails,
                        {
                            id: 0,
                            userId: newEmail.id,
                            displayName: newEmail.displayName,
                            enableBodyProcess: false,
                            enableProgressFeedback: false,
                            notificationEmail: newEmail.notificationEmail,
                            shortNumber: newEmail.number.shortNumber,
                        },
                    ],
                },
            },
        };
        form.updateValues(newData);
        setAdditionalEmail("0");
    }

    function removeEmail(index: number) {
        const newData = {
            ...form.values,
            ...{
                additionalFaxSendingEmails: {
                    emails: [...form.values.additionalFaxSendingEmails.emails.filter((_, i) => i !== index)],
                },
            },
        };
        form.updateValues(newData);
    }

    function updateEmail(index: number, field: UpdateEmailFields, value: boolean) {
        const updatedEmail = form.values.additionalFaxSendingEmails.emails.find((_, i) => i === index);
        updatedEmail[field] = value;
        const newData = {
            ...form.values,
            ...{
                additionalFaxSendingEmails: {
                    emails: [
                        ...form.values.additionalFaxSendingEmails.emails.filter((_, i) => i !== index),
                        updatedEmail,
                    ],
                },
            },
        };
        form.updateValues(newData);
    }

    return (
        <Page title={defineMessage({id: "settings", defaultMessage: "Settings"})}>
            <FormContainer>
                <FormattedFormRow label={defineMessage({id: "corporateName", defaultMessage: "Corporate name"})}>
                    <TextField
                        value={form.values.headerInfo}
                        onChange={(value) => form.updateValues({headerInfo: value})}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "faxNumberInHeader", defaultMessage: "Fax number in header"})}
                >
                    <TextField
                        value={form.values.headerNumber}
                        onChange={(value) => form.updateValues({headerNumber: value})}
                        errors={errorHelper.fieldErrors("headerNumber")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "fax_receiving_email_address",
                        defaultMessage: "Fax receiving email address",
                    })}
                    required
                >
                    <TextField
                        value={form.values.incomingEmailAddress}
                        onChange={(value) => form.updateValues({incomingEmailAddress: value})}
                        errors={errorHelper.fieldErrors("incomingEmailAddress")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "fax_sending_email_address",
                        defaultMessage: "Fax sending email address",
                    })}
                >
                    <TextField
                        value={form.values.senderEmailAddress}
                        onChange={(value) => form.updateValues({senderEmailAddress: value})}
                        errors={errorHelper.fieldErrors("senderEmailAddress")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "fax_sending_password", defaultMessage: "Fax sending password"})}
                    required
                >
                    <TextField
                        type={showPassword ? "text" : "password"}
                        value={form.values.password}
                        onChange={(value) => form.updateValues({password: value})}
                        errors={errorHelper.fieldErrors("password")}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Toggle password visibility"
                                        onClick={handleClickShowPassword}
                                        size="large"
                                    >
                                        {showPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                                    </IconButton>
                                    <IconButton
                                        aria-label="Generate new password"
                                        onClick={generateRandomPassword}
                                        size="large"
                                    >
                                        <OwnerIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "notify_about_receiving",
                        defaultMessage: "Notify about unsuccessful receiving",
                    })}
                >
                    <Checkbox
                        value={form.values.notifyUnsuccessfulReceiving}
                        checked={form.values.notifyUnsuccessfulReceiving}
                        onChange={(event) => form.updateValues({notifyUnsuccessfulReceiving: event.target.checked})}
                        color="primary"
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"})}
                    labelAlign="top"
                >
                    <OutgoingCallBarringWidget
                        masterBarrings={dataSource.domainOutgoingCallBarrings}
                        operatorBarrings={form.values.outgoingCallBarringOperator}
                        adminBarrings={form.values.outgoingCallBarringAdmin}
                        updateOperatorBarrings={(barrings) =>
                            form.updateValues({outgoingCallBarringOperator: barrings})
                        }
                        updateAdminBarrings={(barrings) => form.updateValues({outgoingCallBarringAdmin: barrings})}
                        disabledAdmin={!canEdit}
                        disabledOperator={!canEditOperator}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "NonPaymentBlockingType.label", defaultMessage: "Non-payment blocking"})}
                >
                    <EnumField
                        value={form.values.nonPaymentBlockingType}
                        enum_={{NonPaymentBlockingType}}
                        onChange={(val) =>
                            form.updateValues({
                                nonPaymentBlockingType: val as NonPaymentBlockingType,
                            })
                        }
                        disabled={!canEditOperator}
                        translations={NON_PAYMENT_BLOCKING_TYPE_MESSAGES}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({
                        id: "additional_fax_sending_emails",
                        defaultMessage: "Additional fax sending email addresses",
                    })}
                >
                    <Grid container size={12}>
                        <Grid size={12}>
                            <Table>
                                <TableHead>
                                    <TableRow className={cx(classes.row, classes.header)}>
                                        <TableCell>
                                            <FormattedMessage id="shortNumber" defaultMessage="Short number" />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedMessage id="displayName" defaultMessage="Name" />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedMessage id="email" defaultMessage="Email" />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedMessage id="notifications" defaultMessage="Notifications" />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedMessage id="processBody" defaultMessage="Process body" />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedMessage id="action" defaultMessage="Action" />
                                        </TableCell>
                                    </TableRow>
                                    {form.values.additionalFaxSendingEmails.emails.map((email, index) => (
                                        <TableRowWithErrors
                                            key={index}
                                            className={classes.row}
                                            errors={errorHelper.fieldErrors("additionalFaxSendingEmails", {
                                                contextComparator: (context) =>
                                                    context.email === email.notificationEmail,
                                            })}
                                        >
                                            <TableCell>{email.shortNumber}</TableCell>
                                            <TableCell>{email.displayName}</TableCell>
                                            <TableCell>{email.notificationEmail}</TableCell>
                                            <TableCell>
                                                <Select
                                                    variant="standard"
                                                    fullWidth
                                                    margin="dense"
                                                    value={email.enableProgressFeedback.toString()}
                                                    onChange={(event) =>
                                                        updateEmail(
                                                            index,
                                                            "enableProgressFeedback",
                                                            event.target.value.toString() === "true",
                                                        )
                                                    }
                                                >
                                                    <MenuItem key={email.id} value={"true"}>
                                                        <FormattedMessage id="everything" defaultMessage="Everything" />
                                                    </MenuItem>
                                                    <MenuItem key={email.id} value={"false"}>
                                                        <FormattedMessage
                                                            id="atBeginningAndAtAnd"
                                                            defaultMessage="At beginning and at end"
                                                        />
                                                    </MenuItem>
                                                </Select>
                                            </TableCell>
                                            <TableCell>
                                                <Checkbox
                                                    value={email.enableBodyProcess}
                                                    checked={email.enableBodyProcess}
                                                    onChange={(event) =>
                                                        updateEmail(index, "enableBodyProcess", event.target.checked)
                                                    }
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="secondary"
                                                    size="small"
                                                    onClick={() => removeEmail(index)}
                                                >
                                                    <FontAwesomeIcon icon="trash" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRowWithErrors>
                                    ))}
                                </TableHead>
                            </Table>
                        </Grid>
                        <Grid style={{marginTop: "20px"}} size={12}>
                            <div style={{display: "flex"}}>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    style={{marginRight: "25px"}}
                                    onClick={() => addEmail()}
                                    disabled={
                                        selectedAdditionalEmail === "0" ||
                                        !!form.values.additionalFaxSendingEmails.emails.find(
                                            (email) => email.shortNumber === selectedAdditionalEmail,
                                        )
                                    }
                                >
                                    <FormattedMessage id="add" defaultMessage="Add" />
                                </Button>
                                <Select
                                    variant="standard"
                                    fullWidth
                                    margin="dense"
                                    value={selectedAdditionalEmail}
                                    onChange={(event) => setAdditionalEmail(event.target.value.toString())}
                                >
                                    <MenuItem key={0} value={0}>
                                        <FormattedMessage id="select" defaultMessage="Select" />
                                    </MenuItem>
                                    {extensionsQuery.data?.users.items
                                        .filter((ext) => ext.notificationEmail)
                                        .map((ext) => (
                                            <MenuItem
                                                key={ext.number.shortNumber}
                                                value={ext.number.shortNumber}
                                                disabled={
                                                    !!form.values.additionalFaxSendingEmails.emails.find(
                                                        (email) => email.shortNumber === ext.number.shortNumber,
                                                    )
                                                }
                                            >
                                                {ext.number.shortNumber} - {ext.displayName} - {ext.notificationEmail}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </div>
                        </Grid>
                    </Grid>
                </FormattedFormRow>
                <FormattedFormRow>
                    <div style={{margin: "1em 0"}}>
                        <FormActionButtons
                            mainAction={submit}
                            secondaryAction={() => {
                                form.reset();
                            }}
                            enabled={form.hasChanged && enabled}
                            pending={form.submitting}
                        />
                    </div>
                </FormattedFormRow>
            </FormContainer>
        </Page>
    );
};

const FaxSettingsPage = ({domainId, faxId}: {domainId: number; faxId: number}) => {
    const dataSource = useDataSource(faxId, domainId);

    const domainNameQuery = useDomainNameQuery({
        variables: {id: domainId},
    });

    const randomPasswordQuery = useGetRandomPasswordQuery({
        fetchPolicy: "no-cache",
    });

    const extensionsQuery = useExtensionsQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
    });

    if (dataSource.loading || randomPasswordQuery.loading || extensionsQuery.loading || domainNameQuery.loading)
        return <Loading />;

    return (
        <Content
            dataSource={dataSource}
            randomPasswordQuery={randomPasswordQuery}
            extensionsQuery={extensionsQuery}
            domainName={domainNameQuery.data.domains.items[0].name}
        />
    );
};

export default FaxSettingsPage;
